<template>
    <router-link
        v-if="!notVisibleByPermission"
        :to="route"
        :class="{
            'is-active': isActive,
            'is-not-allowed': locked && !canViewAdministratively,
            'corporate-active': isActive && isCorporate,
        }"
        class="is-align-center is-space-between p-r-none tw:flex tw:w-full tw:min-w-0"
        :title="titleText"
        @click="closeMenu"
    >
        <div
            class="tw:flex tw:flex-nowrap tw:gap-2 tw:flex-1 tw:min-w-0"
            :style="{ display: 'flex', alignItems: 'center' }"
        >
            <component
                :is="menuLeadingIcon"
                v-if="menuLeadingIcon"
                :size="18"
                class="activity-title-icon tw:flex-shrink-0"
                :class="{ 'tw:mt-0.5': !isCorporate }"
                weight="bold"
            />
            <!-- Corporate: the leading icon is vertically centered against the label,
                 so when the label wraps to 2-3 lines the icon sits in the middle of
                 the wrapped block (alignItems: center above). `leading-tight` keeps
                 the wrapped lines compact. `min-w-0 flex-1` lets a long title wrap
                 inside the label instead of pushing the icon onto its own row. -->
            <span
                v-tooltip.right.hover="navTitleTooltip(item.text)"
                class="nav-title-clamp-2 tw:flex-1 tw:min-w-0"
                :class="{ 'tw:leading-tight': isCorporate }"
            >{{ item.text }}</span>
        </div>

        <!-- Corporate signals the active item via blue title + icon (see styles), so
             the active dot is stanbridge-only there. -->
        <span
            v-if="isActive && !isCorporate"
            class="active-dot tw:inline-block tw:min-w-2 tw:min-h-2 tw:bg-[var(--primary-color)]! tw:rounded-full tw:ml-2 tw:flex-shrink-0"
        />

        <activity-generation-status-badge
            v-if="hasGenerationState"
            compact
            :is-generating="isGenerating"
            :generation-status="generationStatus"
            :generation-error="generationError"
            class="tw:ml-2 tw:flex-shrink-0"
        />

        <!-- Corporate: seat the completion check in a 1.75rem box (the PrimeVue Tree
             chevron-toggle width) flush to the right edge so it lines up with the
             section chevron column above it. Use a Phosphor SVG check (not the uil
             webfont e-icon, whose glyph floats mid-em) mirroring the leading
             activity-type icon — size 18, bold. Vertically centered (like the
             leading icon) so it stays aligned with it against a wrapped multi-line
             label; the row's `is-align-center` handles the centering.
             Stanbridge keeps the bare icon. -->
        <template v-if="completed">
            <span
                v-if="isCorporate"
                class="activity-menu-check tw:inline-flex tw:items-center tw:justify-center tw:w-7 tw:flex-shrink-0"
            >
                <ph-check :size="18" weight="bold" class="tw:flex-shrink-0 tw:text-green-600" />
            </span>
            <e-icon v-else icon="check" />
        </template>

        <locked-item
            v-if="locked"
            :item="{
                type: locked && hasPrerequisite ? 'prerequisite' : 'visibleAt',
                prereqTitle: locked && hasPrerequisite ? prereqTitle : null,
                namespace: item.type,
                visibleAt: titleText,
                position: 'is-right',
            }"
        />

        <unlocked-item
            v-if="unlocked"
            :item="{
                type: hasPrerequisite ? 'prerequisite' : 'visibleAt',
                prereqTitle: hasPrerequisite ? prereqTitle : null,
                namespace: item.type,
                visibleAt: titleText,
                position: 'is-right',
            }"
        />
    </router-link>
</template>

<script>
import { PhCheck } from '@phosphor-icons/vue';
import has from 'lodash/has.js';

import ActivityGenerationStatusBadge from '@/components/activity-types/course-activity-types/activities/shared/ActivityGenerationStatusBadge.vue';
import LockedItem from '@/components/nested-list/LockedItem.vue';
import UnlockedItem from '@/components/nested-list/UnlockedItem.vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { getActivityDisplayIcon, normalizeActivityTypeKey } from '@/constants/activityDisplayMeta.js';
import { ACTIVITY_GENERATION_STATUS_UI_ENABLED } from '@/constants/activityGenerationStatusFlag.js';
import ActiveStatusMixin from '@/mixins/ActiveStatus.mixin.js';
import SidebarMixin from '@/mixins/Sidebar.mixin.js';
import { getActivityIcon } from '@/utilities/ActivityIcons.js';
import { navTitleTooltip } from '@/utilities/longTitleTooltip.js';

export default {
    name: 'NestedActivity',

    components: { ActivityGenerationStatusBadge, LockedItem, PhCheck, UnlockedItem },

    mixins: [ActiveStatusMixin, SidebarMixin],

    props: {
        closeMenu: {
            type: Function,
            default: () => {},
        },
    },

    emits: ['expand'],

    computed: {
        isCorporate() {
            return isCorporateClient();
        },

        activityTypeKey() {
            const raw = this.item.activity_type_key ?? this.item.activity_type;

            return normalizeActivityTypeKey(raw) || null;
        },

        activityMenuIcon() {
            return getActivityDisplayIcon(this.activityTypeKey);
        },

        menuLeadingIcon() {
            if (!this.activityTypeKey) {
                return null;
            }

            const raw = this.item.activity_type_key ?? this.item.activity_type;
            return getActivityIcon(raw) || this.activityMenuIcon;
        },

        route() {
            if (this.locked && !this.canViewAdministratively) return {};

            if (!has(this.item, 'course_id') || !has(this.item, 'activity_id')) {
                throw new Error(
                    `Course id must be present on the item that is passed to <activity>. Received: ${JSON.stringify(
                        this.item
                    )}`
                );
            }

            return {
                name: 'activity-view',
                params: {
                    courseId: this.item.course_id,
                    activityId: this.item.activity_id,
                },
            };
        },

        completed() {
            if (!this.$store.getters['Activities/has'](this.item.uuid)) {
                return this.item.completed;
            }

            return this.$store.getters[`Activities/${this.item.uuid}/get`]('completed');
        },

        isGenerating() {
            if (this.$store.getters['Activities/has'](this.item.uuid)) {
                return Boolean(this.$store.getters[`Activities/${this.item.uuid}/get`]('is_generating'));
            }

            return Boolean(this.item.is_generating);
        },

        generationStatus() {
            if (this.$store.getters['Activities/has'](this.item.uuid)) {
                return this.$store.getters[`Activities/${this.item.uuid}/get`]('generation_status') ?? null;
            }

            return this.item.generation_status ?? null;
        },

        generationError() {
            if (this.$store.getters['Activities/has'](this.item.uuid)) {
                return this.$store.getters[`Activities/${this.item.uuid}/get`]('generation_error') ?? null;
            }

            return this.item.generation_error ?? null;
        },

        hasGenerationState() {
            if (!ACTIVITY_GENERATION_STATUS_UI_ENABLED) return false;

            return this.isGenerating || ['failed', 'completed'].includes(this.generationStatus);
        },
    },

    created() {
        if (this.isActive) {
            this.$emit('expand', this.item.uuid);
        }
    },

    methods: {
        navTitleTooltip,
    },
};
</script>

<style scoped>
/* Corporate: the active menu item is signalled by colouring its title + leading
   icon with the brand primary (blue) colour instead of a trailing dot (the dot
   is stanbridge-only); the row keeps its shade. The scoped attribute selector +
   !important outranks the Tree's inherited `text-[var(--p-tree-node-color)]!`
   utility on this same root element, so the label (which inherits the link
   colour) turns blue. The completion check keeps its own `is-success` colour. */
.corporate-active {
    color: var(--primary-color) !important;
}

/* The leading icon carries `.activity-title-icon`, which the corporate shell
   styles GLOBALLY (non-scoped) to `color: var(--text-color)` — that explicit
   colour beats inheritance, so we override it explicitly for the active row. */
.corporate-active :deep(.activity-title-icon) {
    color: var(--primary-color) !important;
}

/* The label text is coloured directly (not just via inheritance) by the menu's
   node styles, so inheriting the active link colour isn't enough — colour the
   label element explicitly too. */
.corporate-active .nav-title-clamp-2 {
    color: var(--primary-color) !important;
}

/* Completion check is vertically centered against the leading icon (2026
   sidebar retune); the row's `is-align-center` already handles this, so no
   `align-self` override is needed here. */
</style>
